import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import {
  getSeriesById,
  getSeasonsBySeries,
  getEpisodesBySeason,
  getPlayEpisode,
} from "../services/seriesService";
import { useSelector } from "react-redux";
import Loader from "./Loader/Loader";
import { Play, Info, Calendar, Clock, UserCircle } from "lucide-react";
import SeriesPlayer from "./SeriesPlayer";

const SeriesDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const autoplay = searchParams.get("autoplay") === "true";
  const autoplayEpisodeId = searchParams.get("episodeId");

  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [playingEpisode, setPlayingEpisode] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [seasonsError, setSeasonsError] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);
  const [episodesError, setEpisodesError] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(true);
  const [showAuthMessage, setShowAuthMessage] = useState(false);

  const renderAuthenticationMessage = () => (
    <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-black to-gray-900 p-4">
      <div className="bg-black/60 p-6 rounded-lg max-w-md text-center">
        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <UserCircle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold mb-2 text-white">Acceso Exclusivo</h2>
        <p className="text-gray-300 mb-6">
          Para disfrutar de este contenido necesitas iniciar sesión o
          registrarte en nuestra plataforma.
        </p>
      </div>
    </div>
  );

  //FIX PARA EL TIEMPO EN MINUTOSS
  const formatRuntimeMinutes = (runtime) => {
    if (runtime == null) return "—";
    const n = Number(runtime);
    if (Number.isNaN(n)) return "—";
    // Si es grande (>= 100), asumimos que viene en segundos y convertimos a minutos.
    // Si es chico (típico 20–60), lo tomamos como minutos.
    const minutes = n >= 100 ? Math.round(n / 60) : Math.round(n);
    return `${minutes}m`;
  };

  const loadEpisodesForSeason = async (seasonId) => {
    try {
      setLoadingEpisodes(true);
      setEpisodesError(null);
      setEpisodes([]);

      console.log("Cargando episodios para la temporada ID:", seasonId);
      const response = await getEpisodesBySeason(seasonId);
      console.log("Respuesta de episodios completa:", response);

      // Manejar tanto respuesta directa como formato {success, data}
      let episodes;
      if (response && response.success && response.data) {
        // Formato antiguo {success, data}
        episodes = response.data;
      } else {
        // Respuesta directa
        episodes = response;
      }

      console.log("Episodios procesados:", episodes);
      setEpisodes(episodes || []);
    } catch (err) {
      console.error("Error al cargar episodios:", err);
      setEpisodesError("No se pudieron cargar los episodios");
    } finally {
      setLoadingEpisodes(false);
    }
  };

  useEffect(() => {
    const fetchSeriesDetails = async () => {
      try {
        setLoading(true);
        const response = await getSeriesById(id);

        console.log("API response:", response);

        if (response && response.success && response.data) {
          console.log("Serie detallada:", response.data);

          // Agregar log detallado para seasons y episodes
          console.log("¿Tiene seasons?", !!response.data.seasons);
          if (response.data.seasons) {
            console.log("Número de temporadas:", response.data.seasons.length);
            console.log("Primera temporada:", response.data.seasons[0]);

            if (
              response.data.seasons.length > 0 &&
              response.data.seasons[0].episodes
            ) {
              console.log(
                "Número de episodios en primera temporada:",
                response.data.seasons[0].episodes.length
              );
            } else {
              console.log(
                "No hay episodios en la primera temporada o estructura incorrecta"
              );
            }
          }

          // Cargar la serie
          setSeries(response.data);

          // Intentamos cargar las temporadas por separado
          await fetchSeasons(id);
        } else {
          setError("No se pudo cargar la información de la serie");
        }
      } catch (err) {
        console.error("Error al obtener detalles de la serie:", err);
        setError("Error al cargar los detalles de la serie");
      } finally {
        setLoading(false);
      }
    };

    // Función para procesar el autoplay si se especifica en la URL
    const handleAutoplay = async () => {
      if (autoplay && autoplayEpisodeId) {
        setError(null);
        setHasActiveSubscription(true);

        const token = localStorage.getItem("token");
        if (!token) {
          const nextPath = `/series/${id}?autoplay=true&episodeId=${autoplayEpisodeId}`;
          localStorage.setItem("nextAfterLogin", nextPath);
          localStorage.removeItem("intendedPlaybackPath");
          localStorage.removeItem("intendedPlayback");
          setShowAuthMessage(true);
          return;
        }

        try {
          const episodeData = await getPlayEpisode(autoplayEpisodeId);
          console.log("Respuesta de episodio para autoplay:", episodeData);

          if (episodeData && episodeData.videoUrl) {
            // Buscar a qué temporada pertenece este episodio y seleccionarla
            if (episodeData.seasonId) {
              // Esto establecerá la temporada seleccionada y cargará sus episodios
              const seasons = await getSeasonsBySeries(id);
              if (seasons && seasons.length > 0) {
                const targetSeason = seasons.find(
                  (s) => s.id === episodeData.seasonId
                );
                if (targetSeason) {
                  console.log(
                    "Seleccionando temporada para autoplay:",
                    targetSeason
                  );
                  setSelectedSeason(targetSeason);

                  // Cargamos los episodios para mostrarlos en la interfaz
                  await loadEpisodesForSeason(targetSeason.id);
                }
              }
            }

            // Reproducir el episodio
            setSelectedEpisode(episodeData);
            setPlayingEpisode({
              ...episodeData,
              videoUrl: episodeData.videoUrl,
              srtUrl: episodeData.srtUrl,
            });

            // Desplazar la vista al reproductor
            setTimeout(() => {
              const videoElement = document.getElementById("episode-player");
              if (videoElement) {
                videoElement.scrollIntoView({ behavior: "smooth" });
              }
            }, 500);
          } else {
            setError(
              "No se pudo cargar el episodio para reproducción automática"
            );
          }
        } catch (err) {
          console.error("Error al procesar autoplay:", err);
          const status = err?.response?.status;
          const reason = err?.response?.data?.reason;

          if (
            status === 403 ||
            reason === "subscription_required" ||
            reason === "inactive_subscription"
          ) {
            setHasActiveSubscription(false);
            const subs = err?.response?.data?.subscriptions || [];
            setSubscriptions(Array.isArray(subs) ? subs : []);
            return;
          }

          setError("Error al reproducir episodio automáticamente");
        }
      }
    };

    const fetchSeasons = async (seriesId) => {
      try {
        setLoadingSeasons(true);
        setSeasonsError(null);

        console.log("Cargando temporadas para serie ID:", seriesId);
        const response = await getSeasonsBySeries(seriesId);
        console.log("Respuesta de temporadas completa:", response);

        // Manejar tanto respuesta directa como formato {success, data}
        let seasons;
        if (response && response.success && response.data) {
          // Formato antiguo {success, data}
          seasons = response.data;
        } else {
          // Respuesta directa
          seasons = response;
        }

        console.log("Temporadas procesadas:", seasons);
        setSeasons(seasons || []);

        // Si hay temporadas, seleccionar la primera por defecto y cargar sus episodios
        if (seasons && seasons.length > 0) {
          const firstSeason = seasons[0];
          setSelectedSeason(firstSeason);

          // Cargar automáticamente los episodios de la primera temporada
          try {
            await loadEpisodesForSeason(firstSeason.id);
          } catch (episodeErr) {
            console.error(
              "Error al cargar episodios de la primera temporada:",
              episodeErr
            );
            // No establecer error aquí para evitar bloquear la interfaz
          }
        }
      } catch (err) {
        console.error("Error al cargar temporadas:", err);
        setSeasonsError("No se pudieron cargar las temporadas de la serie");
      } finally {
        setLoadingSeasons(false);
      }
    };

    const handlePlayEpisode = async (episode) => {
      try {
        // Verificar token
        const token = localStorage.getItem("token");

        if (!token) {
          const nextPath = `/series/${id}?autoplay=true&episodeId=${episode.id}`;
          localStorage.setItem("nextAfterLogin", nextPath);
          localStorage.removeItem("intendedPlaybackPath");
          localStorage.removeItem("intendedPlayback");
          setShowAuthMessage(true);
          return;
        }

        console.log("Solicitando reproducción para episodio ID:", episode.id);
        const response = await getPlayEpisode(episode.id);
        console.log("Respuesta de play episode (COMPLETA):", response);

        // Manejar tanto respuesta directa como formato {success, data}
        let episodeData;
        if (response && response.success && response.data) {
          // Formato antiguo {success, data}
          episodeData = response.data;
          console.log("Usando formato {success, data}");
        } else {
          // Respuesta directa
          episodeData = response;
          console.log("Usando respuesta directa");
        }

        console.log("Datos de episodio procesados:", episodeData);
        console.log("¿Contiene videoUrl?", !!episodeData.videoUrl);
        if (episodeData.videoUrl) {
          console.log("URL del video:", episodeData.videoUrl);
        }

        if (episodeData && episodeData.videoUrl) {
          setPlayingEpisode({
            ...episode,
            videoUrl: episodeData.videoUrl,
            srtUrl: episodeData.srtUrl,
          });

          console.log("Episodio configurado para reproducción:", {
            ...episode,
            videoUrl: episodeData.videoUrl,
            srtUrl: episodeData.srtUrl,
          });

          // Desplazar la vista al reproductor
          setTimeout(() => {
            const videoElement = document.getElementById("episode-player");
            if (videoElement) {
              videoElement.scrollIntoView({ behavior: "smooth" });
            }
          }, 500);
        } else {
          setError("El episodio no contiene una URL de video válida");
        }
      } catch (err) {
        console.error("Error al reproducir episodio:", err);
        if (err.response) {
          console.error(
            "Detalles del error:",
            err.response.status,
            err.response.data
          );

          // Verificar si el error es 403 (falta de suscripción)
          if (err.response.status === 403) {
            console.log("Error de suscripción no activa");
            setHasActiveSubscription(false);
            // Obtener las suscripciones del usuario si las hay
            if (err.response.data && err.response.data.subscriptions) {
              setSubscriptions(err.response.data.subscriptions);
            }
            return;
          }
        }
        setError(
          "Error al cargar el episodio para reproducción: " +
            (err.message || "Error desconocido")
        );
      }
    };

    if (id) {
      fetchSeriesDetails().then(() => {
        // Solo procesamos el autoplay después de cargar los detalles de la serie
        handleAutoplay();
      });
    }
  }, [id, autoplay, autoplayEpisodeId]);

  const handleSeasonSelect = async (season) => {
    setSelectedSeason(season);
    setSelectedEpisode(null);
    setPlayingEpisode(null);

    // Cargar episodios de la temporada seleccionada
    await loadEpisodesForSeason(season.id);
  };

  const handleEpisodeSelect = (episode) => {
    setSelectedEpisode(episode);
  };

  const renderNoSubscriptionMessage = () => {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/80 z-50">
        <div className="bg-black/60 p-6 rounded-lg max-w-md text-center">
          <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Play size={16} fill="white" stroke="white" />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-white">
            Suscripción Requerida
          </h2>
          <p className="text-gray-300 mb-4">
            Necesitas tener una suscripción dentro del periodo de vigencia para
            reproducir episodios de series.
          </p>
          {subscriptions && subscriptions.length > 0 ? (
            <div className="mb-4 text-left text-sm bg-gray-800/50 p-3 rounded">
              <p className="font-semibold text-white mb-1">
                Estado de tus suscripciones:
              </p>
              {subscriptions.map((sub) => (
                <div key={sub.id} className="mb-2 text-gray-300">
                  <p>
                    • Estado:{" "}
                    <span
                      className={
                        sub.status === "active"
                          ? "text-green-400"
                          : "text-red-400"
                      }
                    >
                      {sub.status}
                    </span>
                  </p>
                  <p>
                    • Válida: {new Date(sub.startDate).toLocaleDateString()} a{" "}
                    {new Date(sub.endDate).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 mb-4">
              No tienes ninguna suscripción registrada.
            </p>
          )}
          <Link
            to="/subscriptionplan"
            className="block w-full bg-yellow-500 hover:bg-yellow-400 text-black font-medium py-2 px-4 rounded transition-colors"
          >
            Ver planes disponibles
          </Link>
        </div>
      </div>
    );
  };

  if (loading && !series) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !hasActiveSubscription) {
    return renderNoSubscriptionMessage();
  }

  if (error && !loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-red-900/50 border border-red-500 text-white p-8 rounded-md max-w-xl">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (showAuthMessage) {
    return renderAuthenticationMessage();
  }

  return (
    <div className="series-detail-container bg-gray-900 text-white min-h-screen p-6 relative">
      {!hasActiveSubscription && renderNoSubscriptionMessage()}

      {!loading && series && (
        <div>
          {/* Hero Banner con Gradiente al estilo Netflix */}
          <div
            className="relative w-full h-[50vh] bg-cover bg-center"
            style={{
              backgroundImage: `url(${series.banner || series.poster})`,
              backgroundPosition: "center 20%",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/60 to-black"></div>

            {/* Contenido del Hero */}
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="container mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  {series.name}
                </h1>

                <div className="flex flex-wrap gap-2 mb-4 text-sm text-gray-300">
                  {series.releaseYear && (
                    <span className="flex items-center">
                      <Calendar size={16} className="mr-1" />
                      {series.releaseYear}
                    </span>
                  )}
                  <span className="px-1 border border-gray-500">12+</span>
                  <span>
                    {series.totalSeasons || (seasons && seasons.length)}{" "}
                    Temporadas
                  </span>
                  <span className="px-1 border border-gray-500">HD</span>
                </div>

                <div className="max-w-2xl mb-6">
                  <p className="text-base">{series.description}</p>
                </div>

                {(() => {
                  const raw = series.genres ?? series.genre; // acepta ambos
                  let list = [];

                  if (Array.isArray(raw)) {
                    list = raw
                      .map((g) =>
                        typeof g === "string"
                          ? g.trim()
                          : String(g?.name || g?.title || g?.label || "").trim()
                      )
                      .filter(Boolean);
                  } else if (typeof raw === "string") {
                    list = raw
                      .split(",")
                      .map((g) => g.trim())
                      .filter(Boolean);
                  }

                  // Si no hay géneros válidos, no renderizar nada (así no aparece línea ni label)
                  if (!list.length) return null;

                  return (
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="text-gray-400">Géneros: </span>
                      <span className="text-white">{list.join(", ")}</span>
                    </div>
                  );
                })()}

                {/* <div className="flex flex-wrap gap-2 mb-4">
                  <span className="text-gray-400">Géneros: </span>
                  {series.genres && typeof series.genres === 'string' && series.genres.split(',').map((genre, idx) => (
                    <span key={idx} className="text-white">
                      {genre.trim()}{idx < series.genres.split(',').length - 1 ? ',' : ''}
                    </span>
                  ))}
                  {series.genres && Array.isArray(series.genres) && series.genres.map((genre, idx) => (
                    <span key={idx} className="text-white">
                      {genre.trim()}{idx < series.genres.length - 1 ? ',' : ''}
                    </span>
                  ))}
                </div> */}
              </div>
            </div>
          </div>

          {/* Reproductor de video (si hay un episodio en reproducción) */}
          {playingEpisode && (
            <div className="container mx-auto px-4 py-8" id="episode-player">
              <h2 className="text-2xl font-bold text-white mb-4">
                {playingEpisode.name}
              </h2>

              {/* Mostrar mensaje de error si no hay URL de video */}
              {!playingEpisode.videoUrl ? (
                <div className="bg-red-800/50 text-white p-4 rounded-md mb-4">
                  No se pudo cargar el video. URL de video no disponible.
                </div>
              ) : (
                /* Componente SeriesPlayer con las propiedades correctas */
                <SeriesPlayer
                  episode={{
                    ...playingEpisode,
                    // Asegurarnos de que estos campos siempre estén definidos
                    videoUrl: playingEpisode.videoUrl || "",
                    srtUrl: playingEpisode.srtUrl || "",
                    id: playingEpisode.id,
                    name: playingEpisode.name || "Episodio sin título",
                    seasonId: playingEpisode.seasonId,
                  }}
                  series={series}
                />
              )}

              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-2">
                  {playingEpisode.name}
                </h3>
                <p className="text-gray-300">{playingEpisode.description}</p>
              </div>
            </div>
          )}

          <div className="container mx-auto px-4 py-8">
            {/* Mensaje de carga de temporadas */}
            {loadingSeasons && (
              <div className="flex items-center text-gray-300 mb-4">
                <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-red-600 rounded-full"></div>
                Cargando temporadas...
              </div>
            )}

            {/* Mensaje de error al cargar temporadas */}
            {seasonsError && (
              <div className="bg-red-900/50 border border-red-500 text-white p-4 rounded-md mb-4">
                {seasonsError}
              </div>
            )}

            {/* Temporadas */}
            {seasons && seasons.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4 flex items-center">
                  <span className="p-2 rounded-full bg-red-600 mr-2 inline-flex items-center justify-center">
                    <Play size={16} fill="white" stroke="white" />
                  </span>
                  Temporadas
                </h2>

                {/* Selector de Temporada (como en AnimeDetail) */}
                <div className="relative max-w-md">
                  <select
                    onChange={(e) => {
                      const selectedSeasonId = parseInt(e.target.value);
                      const season = seasons.find(
                        (s) => s.id === selectedSeasonId
                      );
                      if (season) {
                        handleSeasonSelect(season);
                      }
                    }}
                    value={selectedSeason?.id || ""}
                    className="w-full bg-gray-800 text-white p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 appearance-none cursor-pointer"
                  >
                    <option value="" disabled>
                      Seleccionar Temporada
                    </option>
                    {seasons.map((season) => (
                      <option key={season.id} value={season.id}>
                        {season.title || `Temporada ${season.seasonNumber}`}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {/* Lista de episodios */}
            {selectedSeason && (
              <div>
                <h2 className="text-2xl font-bold mb-6 flex items-center">
                  <span className="p-2 rounded-full bg-gray-700 mr-2 inline-flex items-center justify-center">
                    <Info size={16} className="text-white" />
                  </span>
                  Episodios
                </h2>

                {/* Mensaje de carga de episodios */}
                {loadingEpisodes && (
                  <div className="flex items-center text-gray-300 mb-4">
                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-red-600 rounded-full"></div>
                    Cargando episodios...
                  </div>
                )}

                {/* Mensaje de error al cargar episodios */}
                {episodesError && (
                  <div className="bg-red-900/50 border border-red-500 text-white p-4 rounded-md mb-4">
                    {episodesError}
                  </div>
                )}

                {!loadingEpisodes &&
                  episodes.length === 0 &&
                  !episodesError && (
                    <p className="text-gray-300 py-4 text-center">
                      No hay episodios disponibles para esta temporada.
                    </p>
                  )}

                {episodes.length > 0 && (
                  <div className="space-y-4">
                    {episodes.map((episode) => (
                      <div
                        key={episode.id}
                        className="bg-gray-900 hover:bg-gray-800 rounded-lg overflow-hidden cursor-pointer transition-colors duration-300 border border-gray-800"
                        onClick={async () => {
                          // Marcar este episodio como seleccionado
                          setSelectedEpisode(episode);

                          // Verificar token
                          const token = localStorage.getItem("token");
                          if (!token) {
                            const nextPath = `/series/${id}?autoplay=true&episodeId=${episode.id}`;
                            localStorage.setItem("nextAfterLogin", nextPath);
                            localStorage.removeItem("intendedPlaybackPath");
                            localStorage.removeItem("intendedPlayback");
                            setShowAuthMessage(true);
                            return;
                          }

                          try {
                            console.log(
                              "Solicitando reproducción para episodio ID:",
                              episode.id
                            );
                            const response = await getPlayEpisode(episode.id);
                            console.log(
                              "Respuesta de play episode (COMPLETA):",
                              response
                            );

                            // Manejar tanto respuesta directa como formato {success, data}
                            let episodeData;
                            if (response && response.success && response.data) {
                              // Formato antiguo {success, data}
                              episodeData = response.data;
                              console.log("Usando formato {success, data}");
                            } else {
                              // Respuesta directa
                              episodeData = response;
                              console.log("Usando respuesta directa");
                            }

                            console.log(
                              "Datos de episodio procesados:",
                              episodeData
                            );
                            console.log(
                              "¿Contiene videoUrl?",
                              !!episodeData.videoUrl
                            );
                            if (episodeData.videoUrl) {
                              console.log(
                                "URL del video:",
                                episodeData.videoUrl
                              );
                            }

                            if (episodeData && episodeData.videoUrl) {
                              setPlayingEpisode({
                                ...episode,
                                videoUrl: episodeData.videoUrl,
                                srtUrl: episodeData.srtUrl,
                              });

                              console.log(
                                "Episodio configurado para reproducción:",
                                {
                                  ...episode,
                                  videoUrl: episodeData.videoUrl,
                                  srtUrl: episodeData.srtUrl,
                                }
                              );

                              // Desplazar la vista al reproductor
                              setTimeout(() => {
                                const videoElement =
                                  document.getElementById("episode-player");
                                if (videoElement) {
                                  videoElement.scrollIntoView({
                                    behavior: "smooth",
                                  });
                                }
                              }, 500);
                            } else {
                              setError(
                                "El episodio no contiene una URL de video válida"
                              );
                            }
                          } catch (err) {
                            console.error("Error al reproducir episodio:", err);
                            if (err.response && err.response.status === 403) {
                              setHasActiveSubscription(false);
                              if (
                                err.response.data &&
                                err.response.data.subscriptions
                              ) {
                                setSubscriptions(
                                  err.response.data.subscriptions
                                );
                              }
                              return;
                            }
                            setError(
                              "Error al cargar el episodio para reproducción"
                            );
                          }
                        }}
                      >
                        <div className="flex flex-col md:flex-row">
                          <div className="md:w-1/3 lg:w-1/4 relative">
                            <img
                              src={
                                episode.poster ||
                                series.poster ||
                                "/placeholder-image.jpg"
                              }
                              alt={episode.name}
                              className="w-full h-full object-cover aspect-video"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/placeholder-image.jpg";
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity">
                              <div className="p-3 rounded-full bg-red-600/80">
                                <Play fill="white" className="w-8 h-8" />
                              </div>
                            </div>
                          </div>
                          <div className="p-4 md:w-2/3 lg:w-3/4">
                            <div className="flex justify-between items-start">
                              <h3 className="font-bold text-lg">
                                {episode.name}
                              </h3>
                              <span className="text-sm text-gray-400 flex items-center">
                                <Clock size={14} className="mr-1" />
                                {formatRuntimeMinutes(episode.runtime)}
                              </span>

                              {/* <span className="text-sm text-gray-400 flex items-center">
                                <Clock size={14} className="mr-1" />
                                {Math.floor(episode.runtime / 60)}m{" "}
                                {episode.runtime % 60}s
                              </span> */}
                            </div>
                            <p className="mt-2 text-gray-400 text-sm line-clamp-2">
                              {episode.description ||
                                `Episodio ${episode.episodeNumber} de la temporada ${selectedSeason.seasonNumber}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {loading && <Loader />}
    </div>
  );
};

export default SeriesDetail;
